import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const LocationContext = createContext();
const STORAGE_KEY = 'calma_last_location';

const getCachedLocation = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const { lat, lng, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < 3600000) return { lat, lng };
    }
  } catch {}
  return null;
};

const saveCachedLocation = (lat, lng) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ lat, lng, timestamp: Date.now() }));
  } catch {}
};

export const LocationProvider = ({ children }) => {
  const cached = getCachedLocation();
  const [location, setLocation] = useState(cached || { lat: 28.2916, lng: -16.6291 });
  const [isRealLocation, setIsRealLocation] = useState(!!cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const watchRef = useRef(null);

  const handlePosition = useCallback((pos) => {
    const { latitude: lat, longitude: lng } = pos.coords;
    setLocation({ lat, lng });
    setIsRealLocation(true);
    setPermissionDenied(false);
    setLoading(false);
    saveCachedLocation(lat, lng);
  }, []);

  useEffect(() => {
    if (!('geolocation' in navigator)) { setLoading(false); return; }

    navigator.geolocation.getCurrentPosition(
      handlePosition,
      (err) => { if (err.code === 1) setPermissionDenied(true); setError(err.message); setLoading(false); },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
    );

    watchRef.current = navigator.geolocation.watchPosition(
      handlePosition, () => {},
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );

    return () => { if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current); };
  }, [handlePosition]);

  const refreshLocation = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      handlePosition,
      (err) => { if (err.code === 1) setPermissionDenied(true); setLoading(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  return (
    <LocationContext.Provider value={{ location, isRealLocation, loading, error, permissionDenied, refreshLocation }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) throw new Error('useLocation must be used within a LocationProvider');
  return context;
};
